import { useCallback, useRef, useState } from "react";

type FileStatus = { percent: number; done: boolean };

export const useUploadProgress = () => {
  const filesRef = useRef(new Map<string, FileStatus>());
  const [progress, setProgress] = useState<number | null>(null);

  const recalculate = useCallback(() => {
    const files = filesRef.current;
    const allDone = files.size > 0 && [...files.values()].every((f) => f.done);
    if (allDone) {
      files.clear();
      setProgress(null);
      return;
    }
    if (files.size === 0) {
      setProgress(null);
      return;
    }
    const total = [...files.values()].reduce((sum, f) => sum + (f.done ? 100 : f.percent), 0);
    setProgress(Math.round(total / files.size));
  }, []);

  const trackFile = useCallback(
    (uid: string, percent: number) => {
      filesRef.current.set(uid, { percent, done: false });
      recalculate();
    },
    [recalculate],
  );

  const completeFile = useCallback(
    (uid: string) => {
      const entry = filesRef.current.get(uid);
      if (entry) {
        entry.percent = 100;
        entry.done = true;
      }
      recalculate();
    },
    [recalculate],
  );

  const failFile = useCallback(
    (uid: string) => {
      filesRef.current.delete(uid);
      recalculate();
    },
    [recalculate],
  );

  return { progress, uploading: progress != null, trackFile, completeFile, failFile };
};
